import { Component, HostListener, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { UserService } from '../../_services/users.service';
import { AlertService } from '../../_services/alert.service';
import { PAGES, PAGE_GROUPS, rolePages } from '../../../Shared/_constants/page-access';
import { STAFF_ROLES, staffRole } from './staff-roles';

export interface StaffMember {
  userid: number;
  useremail: string;
  firstname: string;
  lastname: string;
  userrole: string;
  isActive: boolean;
  roles: string[];
  // Pages a super admin limited them to (null = whatever their roles allow).
  allowedPages?: string[] | null;
}

// The list's filters, carried to a person's page (Previous / Next follow them) and back.
export interface StaffListState {
  q?: string;
  role?: string;
  status?: string;
}

// The same filter the list applies - shared with the edit page's Previous / Next.
export function staffMatches(m: StaffMember, s: StaffListState): boolean {
  const status = s.status || 'ALL';
  if (status !== 'ALL' && (status === 'ACTIVE') !== m.isActive) return false;
  if (s.role && s.role !== 'ALL' && !m.roles.includes(s.role)) return false;
  const term = (s.q ?? '').trim().toLowerCase();
  if (!term) return true;
  return m.useremail.toLowerCase().includes(term) || `${m.firstname ?? ''} ${m.lastname ?? ''}`.toLowerCase().includes(term);
}

export function toStaffMember(r: any): StaffMember {
  return {
    ...r,
    isActive: r.isActive !== false && r.isActive !== 0,
    roles: String(r.userrole ?? '').split(',').map((x: string) => x.trim()).filter(Boolean),
  };
}

// Everyone who works here, in one place - this replaces the separate Admin
// Users, Sales Person, Delivery Team and Purchase Team pages. Creating and
// editing happen on their own page (/staff/create, /staff/edit/:id).
@Component({
    selector: 'app-staff',
    imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MainLayoutComponent],
    styles: [`
    .sf-fade { animation: sf-fade .18s ease-out; }
    .sf-slide { animation: sf-slide .24s cubic-bezier(.2,.8,.2,1); }
    @keyframes sf-fade { from { opacity: 0; } }
    @keyframes sf-slide { from { transform: translateX(100%); } }
  `],
    templateUrl: './staff.component.html'
})
export class StaffComponent implements OnInit {
  members: StaffMember[] = [];
  loading = true;
  loadError = false;
  search = '';
  roleFilter = 'ALL';
  statusFilter: 'ALL' | 'ACTIVE' | 'INACTIVE' = 'ALL';

  readonly roleOptions = STAFF_ROLES;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  selected: StaffMember | null = null;

  constructor(
    private userService: UserService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('q')) this.search = qp.get('q')!;
    if (qp.get('role')) this.roleFilter = qp.get('role')!;
    if (qp.get('status')) this.statusFilter = qp.get('status') as any;
    this.load();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selected = null;
  }

  get listState(): StaffListState {
    const s: StaffListState = {};
    if (this.search.trim()) s.q = this.search.trim();
    if (this.roleFilter !== 'ALL') s.role = this.roleFilter;
    if (this.statusFilter !== 'ALL') s.status = this.statusFilter;
    return s;
  }

  view(m: StaffMember): void {
    this.selected = this.selected?.userid === m.userid ? null : m;
  }

  get counts() {
    const active = this.members.filter((m) => m.isActive).length;
    return {
      total: this.members.length,
      active,
      inactive: this.members.length - active,
      admins: this.members.filter((m) => m.isActive && (m.roles.includes('S-ADMIN') || m.roles.includes('ADMIN'))).length,
      limited: this.members.filter((m) => Array.isArray(m.allowedPages)).length,
    };
  }

  // The pages someone can open, grouped like the menu (their roles, or the
  // list a super admin narrowed them to).
  accessGroups(m: StaffMember): { name: string; pages: string[] }[] {
    const keys = Array.isArray(m.allowedPages) ? new Set(m.allowedPages) : rolePages(m.roles);
    return PAGE_GROUPS.map((name) => ({ name, pages: PAGES.filter((p) => p.group === name && keys.has(p.key)).map((p) => p.label) })).filter((g) => g.pages.length);
  }

  accessCount(m: StaffMember): number {
    return this.accessGroups(m).reduce((n, g) => n + g.pages.length, 0);
  }

  roleInfo(role: string) {
    return this.roleOptions.find((r) => r.value === role);
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.userService.getStaff().subscribe({
      next: (rows) => {
        this.members = rows.map(toStaffMember);
        if (this.selected) this.selected = this.members.find((m) => m.userid === this.selected!.userid) ?? null;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = true;
        this.alertService.alert(error?.error?.message || 'Could not load staff', 'error');
      },
    });
  }

  private matchesStatus(m: StaffMember, status = this.statusFilter): boolean {
    return status === 'ALL' || (status === 'ACTIVE') === m.isActive;
  }

  statusCount(status: 'ALL' | 'ACTIVE' | 'INACTIVE'): number {
    return this.members.filter((m) => this.matchesStatus(m, status)).length;
  }

  get visible(): StaffMember[] {
    const s = this.listState;
    return this.members.filter((m) => staffMatches(m, s));
  }

  countFor(role: string): number {
    const inStatus = this.members.filter((m) => this.matchesStatus(m));
    return role === 'ALL' ? inStatus.length : inStatus.filter((m) => m.roles.includes(role)).length;
  }

  initials(m: StaffMember): string {
    const a = (m.firstname || m.useremail || '?')[0];
    const b = (m.lastname || '')[0] || '';
    return (a + b).toUpperCase();
  }

  badge(role: string): string {
    return staffRole(role)?.badgeClass ?? 'bg-neutral-100 text-neutral-600';
  }

  label(role: string): string {
    return staffRole(role)?.label ?? role;
  }

  onCreate(): void {
    this.router.navigate(['/staff/create'], { queryParams: this.listState });
  }

  onEdit(member: StaffMember): void {
    this.router.navigate(['/staff/edit', member.userid], { queryParams: this.listState });
  }
}
